import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dddpApi from "api/axios";
import cmsApi from "api/cmsapi";
import DPATAssessment from "../../DPATAssessment";
import { startDpatAssessmentWithSnapshot } from "../dpatAssessmentWorkflowStats";

const NETWORK_IDLE_MS = 2000;
const DISTRICT_TIMEOUT_MS = 180000;
const MAX_CAPTURE_RETRIES = 3;

const DpatBulkAssessmentStarter = ({
  rows,
  year,
  user,
  onProgress,
  onFinished,
}) => {
  const [index, setIndex] = useState(0);
  const [snapshotRequest, setSnapshotRequest] = useState(null);
  const currentRow = rows[index] || null;

  const inFlightRef = useRef(0);
  const sawRequestRef = useRef(false);
  const collectorReadyRef = useRef(false);
  const captureRequestedRef = useRef(false);
  const processingTokenRef = useRef(null);
  const retryCountRef = useRef(0);
  const idleTimerRef = useRef(null);
  const timeoutRef = useRef(null);
  const succeededRef = useRef([]);
  const failedRef = useRef([]);
  const onProgressRef = useRef(onProgress);
  const onFinishedRef = useRef(onFinished);

  onProgressRef.current = onProgress;
  onFinishedRef.current = onFinished;

  const clearTimers = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    window.clearTimeout(timeoutRef.current);
  }, []);

  const finishDistrict = useCallback((failure) => {
    clearTimers();
    const processed = index + 1;
    if (failure) failedRef.current.push({ ...currentRow, error: failure });
    onProgressRef.current?.({
      processed,
      total: rows.length,
      current: rows[processed]?.districtName || null,
    });

    if (processed >= rows.length) {
      onFinishedRef.current?.({
        succeeded: succeededRef.current,
        failed: failedRef.current,
        total: rows.length,
      });
      return;
    }
    setIndex(processed);
  }, [clearTimers, currentRow, index, rows]);

  const requestSnapshotWhenIdleRef = useRef(() => {});
  requestSnapshotWhenIdleRef.current = () => {
    window.clearTimeout(idleTimerRef.current);
    if (
      !currentRow ||
      !collectorReadyRef.current ||
      !sawRequestRef.current ||
      inFlightRef.current !== 0 ||
      captureRequestedRef.current
    ) {
      return;
    }

    idleTimerRef.current = window.setTimeout(() => {
      if (inFlightRef.current !== 0 || captureRequestedRef.current) return;
      captureRequestedRef.current = true;
      setSnapshotRequest({
        token: `${currentRow.districtId}-${Date.now()}-${retryCountRef.current}`,
      });
    }, NETWORK_IDLE_MS);
  };

  useLayoutEffect(() => {
    const requestInterceptor = (config) => {
      inFlightRef.current += 1;
      sawRequestRef.current = true;
      window.clearTimeout(idleTimerRef.current);
      return config;
    };
    const responseInterceptor = (response) => {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      requestSnapshotWhenIdleRef.current();
      return response;
    };
    const responseErrorInterceptor = (error) => {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      requestSnapshotWhenIdleRef.current();
      return Promise.reject(error);
    };

    const dddpRequestId = dddpApi.interceptors.request.use(requestInterceptor);
    const dddpResponseId = dddpApi.interceptors.response.use(
      responseInterceptor,
      responseErrorInterceptor,
    );
    const cmsRequestId = cmsApi.interceptors.request.use(requestInterceptor);
    const cmsResponseId = cmsApi.interceptors.response.use(
      responseInterceptor,
      responseErrorInterceptor,
    );

    return () => {
      dddpApi.interceptors.request.eject(dddpRequestId);
      dddpApi.interceptors.response.eject(dddpResponseId);
      cmsApi.interceptors.request.eject(cmsRequestId);
      cmsApi.interceptors.response.eject(cmsResponseId);
    };
  }, []);

  useLayoutEffect(() => {
    if (!currentRow) {
      onFinishedRef.current?.({ succeeded: [], failed: [], total: 0 });
      return undefined;
    }

    clearTimers();
    inFlightRef.current = 0;
    sawRequestRef.current = false;
    collectorReadyRef.current = false;
    captureRequestedRef.current = false;
    processingTokenRef.current = null;
    retryCountRef.current = 0;
    setSnapshotRequest(null);
    onProgressRef.current?.({
      processed: index,
      total: rows.length,
      current: currentRow.districtName,
    });

    timeoutRef.current = window.setTimeout(() => {
      finishDistrict("Timed out while loading the district assessment data.");
    }, DISTRICT_TIMEOUT_MS);
    return clearTimers;
  }, [clearTimers, currentRow, finishDistrict, index, rows.length]);

  const handleCollectorReady = useCallback(() => {
    collectorReadyRef.current = true;
    requestSnapshotWhenIdleRef.current();
  }, []);

  const handleSnapshotReady = useCallback(async ({ token, tables }) => {
    if (!currentRow || processingTokenRef.current === token) return;
    processingTokenRef.current = token;

    try {
      const result = await startDpatAssessmentWithSnapshot({
        row: currentRow,
        year,
        user,
        tables,
      });
      succeededRef.current.push({ ...currentRow, ...result });
      finishDistrict();
    } catch (error) {
      finishDistrict(
        error?.response?.data?.message || error?.message || "Failed to start assessment and save snapshot.",
      );
    }
  }, [currentRow, finishDistrict, user, year]);

  const handleSnapshotError = useCallback(({ error }) => {
    if (retryCountRef.current < MAX_CAPTURE_RETRIES) {
      retryCountRef.current += 1;
      captureRequestedRef.current = false;
      processingTokenRef.current = null;
      window.setTimeout(() => requestSnapshotWhenIdleRef.current(), NETWORK_IDLE_MS);
      return;
    }
    finishDistrict(error?.message || "Failed to capture the assessment tables.");
  }, [finishDistrict]);

  const selectedLocation = useMemo(() => {
    if (!currentRow) return null;
    return {
      label: currentRow.districtName,
      value: {
        id: currentRow.districtId,
        region: {
          name: currentRow.regionName,
          slug: currentRow.regionKey,
        },
      },
    };
  }, [currentRow]);

  if (!currentRow) return null;

  return (
    <div aria-hidden="true" style={{ display: "none" }}>
      <DPATAssessment
        key={`${currentRow.districtId}-${year}`}
        selectedLocation={selectedLocation}
        timePeriod={{ value: Number(year), label: Number(year) }}
        bulkSnapshotRequest={snapshotRequest}
        onBulkSnapshotReady={handleSnapshotReady}
        onBulkSnapshotError={handleSnapshotError}
        onBulkSnapshotCollectorReady={handleCollectorReady}
        bulkSnapshotMode
      />
    </div>
  );
};

export default DpatBulkAssessmentStarter;
